/* Independent navigation; native Minimog search, cart and VAT logic are unchanged. */
(() => {
  if (customElements.get('splus-navigation')) return;
  customElements.define('splus-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mobile = this.querySelector('[data-spn-mobile]');
      this.panel = this.querySelector('[data-spn-drawer]');
      this.branches = [...this.querySelectorAll('[data-spn-branch]')];
      this.dropdowns = [...this.querySelectorAll('[data-spn-dropdown]')];
      this.inerted = [];
      this.mobile.addEventListener('toggle', e => {
        if (e.target !== this.mobile) return;
        this.syncMobile();
      }, { signal });
      this.querySelector('[data-spn-close]').addEventListener('click', () => this.closeMobile(), { signal });
      this.querySelector('[data-spn-back]').addEventListener('click', () => this.goBack(), { signal });
      this.branches.forEach(branch => branch.addEventListener('toggle', () => {
        branch.querySelector('summary').setAttribute('aria-expanded', String(branch.open));
        if (branch.open) {
          this.branches.forEach(other => { if (other !== branch) other.open = false; });
          this.mobile.setAttribute('data-submenu-open', '');
          this.panel.scrollTop = 0;
          this.querySelector('[data-spn-back]').focus();
        } else if (!this.branches.some(other => other.open)) this.mobile.removeAttribute('data-submenu-open');
      }, { signal }));
      this.dropdowns.forEach(dropdown => dropdown.addEventListener('toggle', () => {
        if (dropdown.open) this.closeDropdowns(dropdown);
        dropdown.querySelector('summary').setAttribute('aria-expanded', String(dropdown.open));
      }, { signal }));
      this.querySelectorAll('summary').forEach(summary => summary.setAttribute('aria-expanded', 'false'));
      document.addEventListener('click', e => {
        if (!this.contains(e.target)) this.closeDropdowns();
        if (this.mobile.open && !this.panel.contains(e.target) && !this.mobile.querySelector('summary').contains(e.target)) this.closeMobile();
      }, { signal });
      this.addEventListener('keydown', e => this.onKeydown(e), { signal });
      this.panel.querySelectorAll('a').forEach(link => link.addEventListener('click', () => this.closeMobile(false), { signal }));
      this.querySelector('.spn-search button')?.addEventListener('click', () => this.closeDropdowns(), { signal });
      this.querySelector('.spn-cart')?.addEventListener('click', () => this.closeDropdowns(), { signal });
      this.onResize = () => {
        if (window.innerWidth >= 1024) this.closeMobile(false);
        else this.closeDropdowns();
        this.setHeights();
      };
      window.addEventListener('resize', this.onResize, { signal });
      this.observer = new ResizeObserver(() => this.setHeights());
      this.observer.observe(this.querySelector('.spn-header'));
      this.setHeights();
      this.enhanceCart();
      const cartDrawer = document.querySelector('m-cart-drawer');
      if (cartDrawer) {
        this.cartObserver = new MutationObserver(() => this.enhanceCart());
        this.cartObserver.observe(cartDrawer, { childList: true, subtree: true });
      }
      // Space activates the native cart's link-as-button, as expected for keyboard users.
      this.querySelector('.spn-cart')?.addEventListener('keydown', e => {
        if (e.key === ' ' && e.currentTarget.getAttribute('role') === 'button') { e.preventDefault(); e.currentTarget.click(); }
      }, { signal });
    }
    enhanceCart() {
      const drawer = document.querySelector('m-cart-drawer');
      if (!drawer) return;
      drawer.classList.add('spn-cart-enhanced');
      const coupon = drawer.querySelector('[data-open="coupon"]');
      if (coupon) coupon.setAttribute('aria-label', document.documentElement.lang.startsWith('en') ? 'Discount code' : 'كود الخصم');
      const title = drawer.querySelector('.m-cart-drawer__title');
      if (title && !title.querySelector('.spn-cart-title-icon')) {
        title.insertAdjacentHTML('afterbegin', '<span class="spn-cart-title-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2"/></svg></span>');
      }
      const empty = drawer.querySelector('.m-cart-drawer__empty');
      if (empty && !empty.dataset.spnEnhanced) {
        const catalogUrl = this.querySelector('.spn-all-products')?.href || this.querySelector('.spn-catalog-compact')?.href || '/collections/all';
        empty.dataset.spnEnhanced = 'true';
        empty.classList.add('spn-cart-empty');
        empty.innerHTML = `<span class="spn-cart-empty-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2"/><path d="M9 13h6"/></svg></span><strong>سلتك جاهزة لاختياراتك</strong><p>أضف المنتجات التي تناسبك، وراجع السعر والكمية بوضوح قبل إتمام الطلب.</p><a href="${catalogUrl}">تصفح المنتجات</a>`;
      }
      const checkout = drawer.querySelector('button[name="checkout"]');
      if (checkout && !checkout.querySelector('.spn-cart-checkout-icon')) {
        checkout.insertAdjacentHTML('afterbegin', '<span class="spn-cart-checkout-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="10" width="14" height="11" rx="3"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg></span>');
      }
      clearTimeout(this.shippingProgressTimer);
      this.shippingProgressTimer = setTimeout(() => this.updateShippingProgress(drawer), 120);
    }
    async updateShippingProgress(drawer) {
      const requestId = (this.shippingProgressRequest || 0) + 1;
      this.shippingProgressRequest = requestId;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        if (requestId !== this.shippingProgressRequest || !drawer.isConnected) return;
        let goal = drawer.querySelector('.spn-cart-goal');
        if (!cart.item_count) { goal?.remove(); return; }
        const goalMinor = Math.round((Number(this.dataset.freeShippingGoal) || 599) * 100);
        // The customer-facing target is VAT-inclusive; never add VAT to the target.
        const displayGoalMinor = goalMinor;
        let vatFactor = 1.15;
        try {
          const config = JSON.parse(document.querySelector('#splus-vat-config')?.textContent || '{}');
          if (Number(config.percent) >= 100) vatFactor = Number(config.percent) / 100;
        } catch (_) { /* Saudi storefront display defaults to 15% VAT. */ }
        const items = cart.items || [];
        const lineTotal = items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
        const inclusiveTotal = items.reduce((sum, item) => sum + Math.round(Number(item.final_line_price || 0) * (item.taxable ? vatFactor : 1)), 0);
        const displayMinor = Math.round(lineTotal > 0 ? inclusiveTotal * cart.total_price / lineTotal : 0);
        const remainingMinor = Math.max(0, displayGoalMinor - displayMinor);
        const percent = Math.min(100, Math.max(0, displayMinor / displayGoalMinor * 100));
        if (!goal) {
          goal = document.createElement('div');
          goal.className = 'spn-cart-goal';
          goal.innerHTML = '<div class="spn-cart-goal__copy"><span data-spn-goal-message></span><strong data-spn-goal-value></strong></div><div class="spn-cart-goal__track" role="progressbar" aria-label="التقدم نحو الشحن المجاني"><span data-spn-goal-bar></span></div>';
          drawer.querySelector('.m-cart-drawer__title')?.insertAdjacentElement('afterend', goal);
        }
        const formatter = new Intl.NumberFormat('ar-SA-u-nu-latn', { style: 'currency', currency: cart.currency || 'SAR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
        const english = document.documentElement.lang.startsWith('en');
        const message = english ? `Free shipping from ${formatter.format(displayGoalMinor / 100)}` : `شحن مجاني من ${formatter.format(displayGoalMinor / 100)}`;
        const value = remainingMinor > 0 ? (english ? `${Math.ceil(remainingMinor / 100)} SAR to go` : `باقي ${Math.ceil(remainingMinor / 100)} ر.س`) : (english ? '✓ Unlocked' : '✓ اكتمل');
        const messageNode = goal.querySelector('[data-spn-goal-message]');
        const valueNode = goal.querySelector('[data-spn-goal-value]');
        const bar = goal.querySelector('[data-spn-goal-bar]');
        const track = goal.querySelector('[role="progressbar"]');
        if (messageNode.textContent !== message) messageNode.textContent = message;
        if (valueNode.textContent !== value) valueNode.textContent = value;
        const width = `${percent.toFixed(2)}%`;
        if (bar.style.width !== width) bar.style.width = width;
        track.setAttribute('aria-label', message);
        track.setAttribute('aria-valuetext', value);
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', String(displayGoalMinor / 100));
        track.setAttribute('aria-valuenow', String(Math.min(displayMinor, displayGoalMinor) / 100));
        goal.classList.toggle('is-complete', remainingMinor === 0);
      } catch (error) {
        console.warn('تعذر تحديث شريط الشحن المجاني.', error);
      }
    }
    setHeights() {
      const height = Math.ceil(this.querySelector('.spn-header').getBoundingClientRect().height);
      document.documentElement.style.setProperty('--spn-header-height', `${height}px`);
      document.documentElement.style.setProperty('--m-header-height', `${height}px`);
      document.documentElement.style.setProperty('--m-topbar-height', '0px');
      if (window.MinimogSettings) window.MinimogSettings.headerHeight = height;
    }
    closeDropdowns(except) { this.dropdowns.forEach(el => { if (el !== except) el.open = false; }); }
    syncMobile() {
      const open = this.mobile.open;
      this.mobile.querySelector('summary').setAttribute('aria-expanded', String(open));
      this.closest('.spn-section')?.classList.toggle('spn-modal-open', open);
      document.documentElement.classList.toggle('spn-scroll-lock', open);
      if (open) {
        this.closeDropdowns();
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        let branch = this.mobile;
        while (branch.parentElement && branch !== document.body) {
          [...branch.parentElement.children].forEach(sibling => {
            if (sibling !== branch && !sibling.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
              sibling.inert = true; this.inerted.push(sibling);
            }
          });
          branch = branch.parentElement;
        }
        this.panel.querySelector('[data-spn-close]').focus();
      } else {
        this.releaseInert();
        this.panel.removeAttribute('role');
        this.panel.removeAttribute('aria-modal');
        this.branches.forEach(el => { el.open = false; });
        this.mobile.removeAttribute('data-submenu-open');
      }
    }
    releaseInert() { this.inerted.forEach(el => { el.inert = false; }); this.inerted = []; }
    closeMobile(restoreFocus = true) {
      if (!this.mobile.open) return;
      this.mobile.open = false;
      this.syncMobile();
      if (restoreFocus) this.mobile.querySelector('summary').focus();
    }
    goBack() {
      const current = this.branches.find(el => el.open);
      this.branches.forEach(el => { el.open = false; el.querySelector('summary').setAttribute('aria-expanded', 'false'); });
      this.mobile.removeAttribute('data-submenu-open');
      current?.querySelector('summary').focus();
      this.panel.scrollTop = 0;
    }
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.mobile.open) { e.preventDefault(); this.closeMobile(); return; }
        const open = this.dropdowns.find(el => el.open);
        if (open) { e.preventDefault(); open.open = false; open.querySelector('summary').focus(); }
      }
      if (e.key !== 'Tab' || !this.mobile.open) return;
      const focusable = [...this.panel.querySelectorAll('a[href],button:not([disabled]),summary')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); this.cartObserver?.disconnect(); clearTimeout(this.shippingProgressTimer); this.shippingProgressRequest = (this.shippingProgressRequest || 0) + 1; this.releaseInert();
      document.documentElement.classList.remove('spn-scroll-lock'); this.controller = null;
    }
  });
})();


